import { useEffect, useRef } from 'react';

const SubtitleTextarea = ({
    index,
    rowData,
    currentIndex,
    checkSub,
    highlightSearchTerm,
    searchTerm,
    handleKeyDown,
    handleOnMouseClick,
    updateSub,
    handleTextChange,
    player,
    subtitle,
    selectedLanguage,
    isCurrentMatch,
    currentMatchIndex,
    matchOffsets,
    readOnly,
}) => {
    const highlightLayerRef = useRef(null);
    const textareaRef = useRef(null);

    useEffect(() => {
        if (highlightLayerRef.current) {
            highlightLayerRef.current.innerHTML = highlightSearchTerm(rowData.text2, searchTerm, index);
        }
    }, [rowData.text2, searchTerm, index, highlightSearchTerm]);

    // Keep highlight layer and textarea scrolling in sync
    useEffect(() => {
        const syncScroll = () => {
            if (textareaRef.current && highlightLayerRef.current) {
                highlightLayerRef.current.scrollTop = textareaRef.current.scrollTop;
                highlightLayerRef.current.scrollLeft = textareaRef.current.scrollLeft;
            }
        };

        if (textareaRef.current) {
            textareaRef.current.addEventListener('scroll', syncScroll);
        }

        return () => {
            if (textareaRef.current) {
                textareaRef.current.removeEventListener('scroll', syncScroll);
            }
        };
    }, []);

    // Focus and select the current match if applicable
    useEffect(() => {
        if (isCurrentMatch && searchTerm && textareaRef.current) {
            const match = matchOffsets[currentMatchIndex];
            if (match) {
                textareaRef.current.focus();
                textareaRef.current.setSelectionRange(match.start, match.end);
            }
        }
    }, [isCurrentMatch, currentMatchIndex, searchTerm, matchOffsets]);

    const containerStyle = {
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    };

    const highlightLayerStyle = {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        padding: '8px',
        whiteSpace: 'pre-wrap',
        wordWrap: 'break-word',
        color: 'transparent',
        backgroundColor: 'transparent',
        pointerEvents: 'none',
        overflow: 'auto',
        zIndex: 1,
        boxSizing: 'border-box',
        textAlign: 'center', // Center align the text
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center' // Vertically center content
    };

    const textareaStyle = {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        padding: '8px',
        backgroundColor: 'transparent',
        color: 'inherit',
        fontSize: '16px',
        lineHeight: '1.5',
        zIndex: 2,
        resize: 'none',
        border: 'none',
        outline: 'none',
        overflow: 'auto',
        boxSizing: 'border-box',
        textAlign: 'center', // Center align the text
        display: 'flex',
        alignItems: 'center', // Helps with vertical centering for textarea
        cursor: readOnly ? 'not-allowed' : 'text',
    };

    return (
        <div
            className={[
                'custom textarea',
                currentIndex === index ? 'highlight' : '',
                checkSub(rowData) ? 'illegal' : '',
            ]
                .join(' ')
                .trim()}
            style={containerStyle}
        >
            <div className="highlight-layer" ref={highlightLayerRef} style={highlightLayerStyle} />
            <textarea
                ref={textareaRef}
                id={`subtitleTextInput${index}`}
                spellCheck="true"
                onKeyDown={(e) => handleKeyDown(e, index, 'text2', player, subtitle, selectedLanguage)}
                onClick={(e) => handleOnMouseClick(e, index, 'text2', player, subtitle)}
                data-field="text2"
                value={rowData.text2 || ''}
                style={textareaStyle}
                readOnly={readOnly}
                onChange={(event) => {
                    if (readOnly) {
                        return;
                    }
                    const updates = { text: event.target.value };
                    if (rowData.text2 !== undefined) {
                        updates.text2 = event.target.value;
                    }
                    updateSub(rowData, updates);
                    handleTextChange(index, event.target.value);
                }}
            />
        </div>
    );
};

export default SubtitleTextarea;
